// src/components/scenes/SceneControl.js
import React, { useState, useContext, useEffect } from 'react';
import { FaExclamationTriangle, FaPlus, FaSyncAlt, FaTheaterMasks } from 'react-icons/fa';
import { AppContext } from '../../context/AppContext';
import SceneCard from './SceneCard';
import SceneEditor from './SceneEditor';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';
import './SceneControl.css';

const SceneControl = () => {
  const { scenes, refreshScenes, isConnected } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showEditor, setShowEditor] = useState(false);
  const [currentScene, setCurrentScene] = useState(null);
  
  // Load scenes
  useEffect(() => {
    const loadScenes = async () => {
      try {
        setLoading(true);
        await refreshScenes();
        setError(null);
      } catch (error) {
        console.error('Error loading scenes:', error);
        setError('Failed to load scenes. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadScenes();
  }, [refreshScenes]);
  
  // Get unique categories from scenes
  const getCategories = () => {
    const categories = new Set(scenes.map(scene => scene.category || 'General'));
    return ['all', ...Array.from(categories)];
  };
  
  // Filter scenes by category
  const getFilteredScenes = () => {
    if (activeCategory === 'all') {
      return scenes;
    }
    
    return scenes.filter(scene => (scene.category || 'General') === activeCategory);
  };
  
  // Open editor for new scene
  const handleAddScene = () => {
    setCurrentScene(null);
    setShowEditor(true);
  };
  
  // Open editor for existing scene
  const handleEditScene = (scene) => {
    setCurrentScene(scene);
    setShowEditor(true);
  };
  
  // Handle scene save
  const handleSaveScene = (scene) => {
    refreshScenes();
  };
  
  // Close editor
  const handleCloseEditor = () => {
    setShowEditor(false);
    setCurrentScene(null);
  };
  
  if (loading) {
    return (
      <div className="scene-loading">
        <LoadingSpinner size="large" text="Loading scenes..." />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="scene-error">
        <div className="error-icon">
          <FaExclamationTriangle />
        </div>
        <h3>Error Loading Scenes</h3>
        <p>{error}</p>
        <button 
          className="btn btn-primary"
          onClick={refreshScenes}
        >
          Try Again
        </button>
      </div>
    );
  }
  
  // If showing editor, render the editor component
  if (showEditor) {
    return (
      <SceneEditor
        scene={currentScene}
        onClose={handleCloseEditor}
        onSave={handleSaveScene}
      />
    );
  }
  
  const categories = getCategories();
  const filteredScenes = getFilteredScenes();
  
  return (
    <div className="scene-control-container">
      <div className="scene-header">
        <div className="scene-title">
          <h2>Scene Control</h2>
          <p>Activate scenes to control multiple devices at once</p>
        </div>
        
        <div className="scene-actions">
          <button 
            className="add-scene-btn"
            onClick={handleAddScene}
          >
            <FaPlus />
            <span>Add Scene</span>
          </button>
          
          <div className="connection-status">
            <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
            <button 
              className="refresh-button"
              onClick={refreshScenes}
              title="Refresh Scenes"
            >
              <FaSyncAlt />
            </button>
          </div>
        </div>
      </div>
      
      <div className="category-filter">
        {categories.map(category => (
          <button 
            key={category}
            className={`category-btn ${activeCategory === category ? 'active' : ''}`}
            onClick={() => setActiveCategory(category)}
          >
            {category === 'all' ? 'All Scenes' : category}
          </button>
        ))}
      </div>
      
      {filteredScenes.length === 0 ? (
        <div className="scene-empty">
          <div className="empty-icon">
            <FaTheaterMasks />
          </div>
          <h3>No Scenes Found</h3>
          <p>
            {activeCategory === 'all' 
              ? 'No scenes are currently available in your system.' 
              : `No scenes found in the "${activeCategory}" category.`}
          </p>
          <button 
            className="btn btn-primary add-first-scene"
            onClick={handleAddScene}
          >
            Create Your First Scene
          </button>
        </div>
      ) : (
        <div className="scenes-grid">
          {filteredScenes.map(scene => (
            <SceneCard 
              key={scene.id}
              scene={scene}
              onEdit={handleEditScene}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SceneControl;